import { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../services/apiClient";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import "./ResetPassword.css";
import logoShort from "../assets/logoShort.png";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState("");
  const [isValidToken, setIsValidToken] = useState(true);
  const baseUrl = process.env.REACT_APP_AUTH_URL;

  useEffect(() => {
    // Extract token from URL query parameter
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      console.log("Token extracted from URL:", tokenFromUrl);
    } else {
      setIsValidToken(false);
      toast.error("Invalid or missing reset token", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
    }
  }, [searchParams, isDark]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    if (field === "password") {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      minLength: password.length >= minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Form submitted!");
    console.log("Token:", token);
    console.log("Form data:", formData);
    console.log("Is loading:", isLoading);

    if (!token) {
      console.log("No token found");
      toast.error("Invalid reset token", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
      return;
    }

    if (!formData.newPassword || !formData.confirmPassword) {
      console.log("Passwords are empty");
      toast.error("Please fill in both password fields", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      console.log("Passwords don't match");
      toast.error("Passwords do not match", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
      return;
    }

    const passwordValidation = validatePassword(formData.newPassword);
    console.log("Password validation:", passwordValidation);
    if (!passwordValidation.isValid) {
      console.log("Password validation failed");
      toast.error("Password does not meet security requirements", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
      return;
    }

    setIsLoading(true);

    try {
      console.log("Sending reset password request with token:", token);
      const response = await apiClient.post(`${baseUrl}/user/reset-password`, {
        token: token,
        newPassword: formData.newPassword,
      });

      console.log("Reset Password Response:", response.data);

      if (response.data.success) {
        toast.success(response.data.message || "Password reset successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: isDark ? "dark" : "light",
        });

        // Clear form and navigate to login
        setFormData({ newPassword: "", confirmPassword: "" });
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Reset Password Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data ||
        error.message ||
        "Failed to reset password";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const passwordValidation = validatePassword(formData.newPassword);

  if (!isValidToken) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-background">
          <div className="background-pattern"></div>
        </div>
        <div className="reset-password-content">
          <div className="reset-password-card">
            <div className="error-state">
              <div className="error-icon">❌</div>
              <h2>Invalid Reset Link</h2>
              <p>The password reset link is invalid or has expired.</p>
              <button
                className="back-to-login-btn"
                onClick={handleBackToLogin}
              >
                <ArrowLeftOutlined />
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-background">
        <div className="background-pattern"></div>
      </div>

      <div className="reset-password-content">
        {/* Left Side - Hero Section */}
        <div className="reset-password-hero">
          <div className="floating-elements">
            <div className="floating-element"></div>
            <div className="floating-element"></div>
            <div className="floating-element"></div>
            <div className="floating-element"></div>
          </div>

          <div className="hero-content">
            <h1 className="hero-title">Reset Your Password</h1>
            <p className="hero-subtitle">
              Create a new secure password for your SimplyBond account.
              Make sure it's strong and memorable.
            </p>

            <div className="hero-features">
              <div className="hero-feature">
                <div className="hero-feature-icon">🔐</div>
                <span>Secure Password Reset</span>
              </div>
              <div className="hero-feature">
                <div className="hero-feature-icon">⚡</div>
                <span>Quick & Easy Process</span>
              </div>
              <div className="hero-feature">
                <div className="hero-feature-icon">🛡️</div>
                <span>Enhanced Security</span>
              </div>
              <div className="hero-feature">
                <div className="hero-feature-icon">✅</div>
                <span>Instant Access</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div className="reset-password-form-section">
          <div className="reset-password-card">
            {/* Theme Toggle */}
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={`Switch to ${isDark ? "light" : "dark"} mode`}
            >
              {isDark ? "☀️" : "🌙"}
            </button>

            {/* Header */}
            <div className="reset-password-header">
              <div className="logo-section">
                <img src={logoShort} alt="SimplyBond" className="logo" />
                <h1 className="title">SimplyBond</h1>
              </div>
              <p className="subtitle">
                Enter your new password to complete the reset process
              </p>
            </div>

            {/* Form */}
            <form
              className="reset-password-form"
              onSubmit={handleSubmit}
              onClick={() => console.log("Form clicked!")}
            >
              <div className="form-group">
                <label className="form-label">
                  <LockOutlined />
                  New Password
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className="form-input password-input"
                    placeholder="Enter your new password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => togglePasswordVisibility("password")}
                  >
                    {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <LockOutlined />
                  Confirm Password
                </label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="form-input password-input"
                    placeholder="Confirm your new password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => togglePasswordVisibility("confirm")}
                  >
                    {showConfirmPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div className="password-strength">
                  <h4>Password Requirements:</h4>
                  <div className="requirement-list">
                    <div className={`requirement ${passwordValidation.minLength ? 'met' : 'not-met'}`}>
                      <CheckCircleOutlined />
                      <span>At least 8 characters</span>
                    </div>
                    <div className={`requirement ${passwordValidation.hasUpperCase ? 'met' : 'not-met'}`}>
                      <CheckCircleOutlined />
                      <span>One uppercase letter</span>
                    </div>
                    <div className={`requirement ${passwordValidation.hasLowerCase ? 'met' : 'not-met'}`}>
                      <CheckCircleOutlined />
                      <span>One lowercase letter</span>
                    </div>
                    <div className={`requirement ${passwordValidation.hasNumbers ? 'met' : 'not-met'}`}>
                      <CheckCircleOutlined />
                      <span>One number</span>
                    </div>
                    <div className={`requirement ${passwordValidation.hasSpecialChar ? 'met' : 'not-met'}`}>
                      <CheckCircleOutlined />
                      <span>One special character</span>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={isLoading}
                onClick={() => console.log("Reset Password button clicked!")}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>

              <button
                type="button"
                className="back-to-login-btn"
                onClick={handleBackToLogin}
              >
                <ArrowLeftOutlined />
                Back to Login
              </button>
            </form>

            {/* Additional Info */}
            <div className="info-section">
              <div className="info-item">
                <LockOutlined className="info-icon" />
                <div className="info-content">
                  <h4>Secure Process</h4>
                  <p>Your new password will be securely encrypted</p>
                </div>
              </div>
              <div className="info-item">
                <CheckCircleOutlined className="info-icon" />
                <div className="info-content">
                  <h4>Immediate Access</h4>
                  <p>You can login immediately after reset</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 